import { Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface ScheduleItem {
  id?: string
  scheduleDate: string
  endDate?: string
  location?: string
  dayNumber: number
}

interface ScheduleFormListProps {
  schedules: ScheduleItem[]
  onChange: (schedules: ScheduleItem[]) => void
}

export function ScheduleFormList({ schedules, onChange }: ScheduleFormListProps) {
  const addSchedule = () => {
    onChange([
      ...schedules,
      {
        scheduleDate: '',
        endDate: '',
        location: '',
        dayNumber: schedules.length + 1,
      },
    ])
  }

  const removeSchedule = (index: number) => {
    const updated = schedules.filter((_, i) => i !== index)
    onChange(updated.map((s, i) => ({ ...s, dayNumber: i + 1 })))
  }

  const updateSchedule = (index: number, field: keyof ScheduleItem, value: string | number) => {
    const updated = schedules.map((s, i) => (i === index ? { ...s, [field]: value } : s))
    onChange(updated)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">日程</Label>
        <Button type="button" variant="outline" size="sm" onClick={addSchedule}>
          <Plus className="mr-1 h-3.5 w-3.5" />
          日程を追加
        </Button>
      </div>

      {schedules.length === 0 && (
        <p className="text-sm text-muted-foreground">日程が登録されていません</p>
      )}

      {schedules.map((schedule, index) => (
        <div key={schedule.id ?? `new-${index}`} className="rounded-md border p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Day {schedule.dayNumber}</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
              onClick={() => removeSchedule(index)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs">開始日時</Label>
              <Input
                type="datetime-local"
                value={schedule.scheduleDate}
                onChange={(e) => updateSchedule(index, 'scheduleDate', e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">終了日時</Label>
              <Input
                type="datetime-local"
                value={schedule.endDate ?? ''}
                onChange={(e) => updateSchedule(index, 'endDate', e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">場所</Label>
            <Input
              placeholder="会議室A、オンラインなど"
              value={schedule.location ?? ''}
              onChange={(e) => updateSchedule(index, 'location', e.target.value)}
            />
          </div>
        </div>
      ))}
    </div>
  )
}
